import React, {
  useState,
  createContext,
  ReactNode,
  useContext,
  useEffect
} from 'react'
import { UserInfo } from '@services/api/users/types'
import { SessionCreate, SessionsApi } from '@services/api/sessions'
import {
  getTokenCookie,
  removeRefreshToken,
  removeToken,
  setRefreshTokenCookie,
  setTokenCookie
} from '@utils/auth-cookie'
import { api } from '@services/api'
import Router from 'next/router'
import { NotAuthorized } from '@components/admin/not-authorized'

interface AuthProviderData {
  user: UserInfo
  hasPermission: (alloweds?: string[]) => boolean
  hasPermissionOrElse: (
    alloweds: string[],
    content: ReactNode,
    orElse?: ReactNode
  ) => JSX.Element
  signIn: (data: SessionCreate) => Promise<void>
  signOut: (redirect?: string) => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext({} as AuthProviderData)

interface AuthProviderProps {
  children: ReactNode
}

export const signOut = async (redirect = '/') => {
  removeToken()
  removeRefreshToken()
  await Router.push(redirect)
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo>()
  const isAuthenticated = !!user

  const authContexSignOut = async (redirect?: string) => {
    setUser(undefined)
    await signOut(redirect)
  }

  useEffect(() => {
    const token = getTokenCookie()

    if (token) {
      api
        .get<UserInfo>('/user_info')
        .then(response => {
          setUser(response.data)
        })
        .catch(() => {
          authContexSignOut()
        })
    }
  }, [])

  const signIn = async (data: SessionCreate) => {
    const response = await SessionsApi.create(data)
    const { token, refresh_token, ...rest } = response.data
    setTokenCookie(token)
    setRefreshTokenCookie(refresh_token)
    setUser(rest)
    api.defaults.headers.Authorization = `Bearer ${token}`
  }

  const hasPermission = (accepteds: string[]): boolean => {
    const token = getTokenCookie()
    if (!token) {
      return false
    }
    return accepteds?.some(role => user?.roles.includes(role))
  }

  const hasPermissionOrElse = (
    alloweds: string[] = [],
    content: ReactNode,
    orElse: ReactNode = <NotAuthorized />
  ): JSX.Element => <>{hasPermission(alloweds) ? content : orElse}</>

  return (
    <AuthContext.Provider
      value={{
        hasPermissionOrElse,
        user,
        hasPermission,
        signIn,
        signOut: authContexSignOut,
        isAuthenticated
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthProviderData => {
  return useContext(AuthContext)
}
